import React, { useState, useEffect, useCallback, useRef } from "react";
import httpService from "../../services/httpService";
import baseURLConfig from "../../baseURL";

const BADGE_THRESHOLDS = { SPECIALIST: 150, GUARDIAN: 250 };
const TOTAL_LEVELS = 7;

function getBadgeTier(score) {
  const s = Number(score) || 0;
  if (s >= BADGE_THRESHOLDS.GUARDIAN) return "guardian";
  if (s >= BADGE_THRESHOLDS.SPECIALIST) return "specialist";
  return "analyst";
}

const TIER_CONFIG = {
  analyst:    { label: "Analyst",    bg: "bg-blue-950",   text: "text-blue-300",   border: "border-blue-500" },
  specialist: { label: "Specialist", bg: "bg-amber-950",  text: "text-amber-300",  border: "border-amber-500" },
  guardian:   { label: "Guardian",   bg: "bg-emerald-950",text: "text-emerald-300",border: "border-emerald-500" },
};

const STATUS_ORDER = { "Completed": 3, "In Progress": 2, "Not Started": 1 };
const STATUS_STYLES = {
  "Completed":  "text-emerald-400",
  "In Progress":"text-amber-400",
  "Not Started":"text-slate-400",
};

function deriveRow(user) {
  const progress   = Number(user.progress) || 0;
  const totalScore = Number(user.totalScore) || 0;
  const gameFinished = user.gameFinished === 1 || user.gameFinished === true;
  const status = gameFinished ? "Completed" : progress >= 1 ? "In Progress" : "Not Started";
  return {
    ...user,
    progress,
    totalScore,
    completion: Math.round((progress / TOTAL_LEVELS) * 100),
    tier: getBadgeTier(totalScore),
    status,
  };
}

const COLUMNS = [
  { key: "user",       label: "Name",           sortVal: r => (r.user      || "").toLowerCase() },
  { key: "emailId",    label: "Email",          sortVal: r => (r.emailId   || "").toLowerCase() },
  { key: "progress",   label: "Level",          sortVal: r => r.progress },
  { key: "completion", label: "Completion %",   sortVal: r => r.completion },
  { key: "totalScore", label: "Defense Score",  sortVal: r => r.totalScore },
  { key: "status",     label: "Status",         sortVal: r => STATUS_ORDER[r.status] || 0 },
];

function generateCSV(rows) {
  const header = ["Name", "Email", "Level", "Completion %", "Defense Score", "Tier", "Status"];
  const lines = rows.map(r => [
    `"${(r.user || "").replace(/"/g, '""')}"`,
    `"${(r.emailId   || "").replace(/"/g, '""')}"`,
    `="${r.progress}/${TOTAL_LEVELS}"`,
    `${r.completion}%`,
    r.totalScore,
    TIER_CONFIG[r.tier]?.label || r.tier,
    r.status,
  ].join(","));
  return [header.join(","), ...lines].join("\n");
}

export default function EducatorDashboard() {
  const EDUCATOR_CODE = process.env.REACT_APP_EDUCATOR_CODE;

  const [unlocked, setUnlocked]       = useState(false);
  const [codeInput, setCodeInput]     = useState("");
  const [codeError, setCodeError]     = useState(false);
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [fetchError, setFetchError]   = useState(null);
  const [sortKey, setSortKey]         = useState("user");
  const [sortDir, setSortDir]         = useState("asc");
  const [search, setSearch]           = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const inputRef = useRef(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const { data } = await httpService.get(baseURLConfig.baseURL);
      const rows = Array.isArray(data) ? data : [];
      setUsers(rows.map(deriveRow));
      setLastRefreshed(new Date());
    } catch {
      setFetchError("Could not reach the backend. Is the server running?");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (unlocked) fetchUsers();
  }, [unlocked, fetchUsers]);

  useEffect(() => {
    if (!unlocked && inputRef.current) inputRef.current.focus();
  }, [unlocked]);

  function handleCodeSubmit(e) {
    e.preventDefault();
    if (EDUCATOR_CODE && codeInput === EDUCATOR_CODE) {
      setUnlocked(true);
      setCodeError(false);
    } else {
      setCodeError(true);
    }
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function handleExportCSV() {
    const csv = generateCSV(filteredSorted);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `OTCyberDefender_players_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const filtered = users.filter(r => {
    const matchesSearch =
      !search ||
      (r.user    || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.emailId || "").toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const col = COLUMNS.find(c => c.key === sortKey);
  const filteredSorted = [...filtered].sort((a, b) => {
    const av = col ? col.sortVal(a) : 0;
    const bv = col ? col.sortVal(b) : 0;
    if (av < bv) return sortDir === "asc" ? -1 :  1;
    if (av > bv) return sortDir === "asc" ?  1 : -1;
    return 0;
  });

  if (!EDUCATOR_CODE) {
    return (
      <div className="min-h-screen w-full relative z-10 bg-slate-950 flex items-center justify-center">
        <div className="text-center space-y-2">
          <div className="text-red-400 text-lg font-mono">⛔ Dashboard not configured</div>
          <div className="text-slate-500 text-sm">REACT_APP_EDUCATOR_CODE is not set in the environment.</div>
        </div>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="min-h-screen w-full relative z-10 bg-slate-950 flex items-center justify-center px-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center space-y-1">
            <div className="text-cyan-400 text-xs font-mono tracking-widest uppercase mb-2">
              OT Cyber Defender
            </div>
            <h1 className="text-2xl font-bold text-white">Educator Dashboard</h1>
            <p className="text-slate-400 text-sm">Enter your access code to continue.</p>
          </div>

          <form onSubmit={handleCodeSubmit} className="space-y-3">
            <input
              ref={inputRef}
              type="password"
              value={codeInput}
              onChange={e => { setCodeInput(e.target.value); setCodeError(false); }}
              placeholder="Access code"
              className={`w-full bg-slate-900 border rounded-lg px-4 py-3 text-white placeholder-slate-500
                          font-mono text-sm focus:outline-none focus:ring-2
                          ${codeError
                            ? "border-red-500 focus:ring-red-500"
                            : "border-slate-700 focus:ring-cyan-500"}`}
            />
            {codeError && (
              <p className="text-red-400 text-xs">Incorrect access code.</p>
            )}
            <button
              type="submit"
              className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold py-3 rounded-lg
                         transition-colors text-sm"
            >
              Unlock Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full relative z-10 bg-slate-950 text-white px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-cyan-400 text-xs font-mono tracking-widest uppercase mb-1">
              OT Cyber Defender
            </div>
            <h1 className="text-2xl font-bold">Educator Dashboard</h1>
            {lastRefreshed && (
              <p className="text-slate-300 text-xs mt-1">
                Last refreshed: {lastRefreshed.toLocaleTimeString()}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchUsers}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700
                         border border-slate-700 rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              <span className={loading ? "animate-spin" : ""}>↻</span>
              {loading ? "Refreshing…" : "Refresh"}
            </button>
            <button
              onClick={handleExportCSV}
              disabled={filteredSorted.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700
                         border border-slate-700 rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              ↓ Export CSV
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-sm
                       text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white
                       focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="all">All Statuses</option>
            <option value="Not Started">Not Started</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>
        </div>

        {/* Summary chips */}
        <div className="flex flex-wrap gap-3 text-xs font-mono">
          {["Not Started", "In Progress", "Completed"].map(s => {
            const count = users.filter(r => r.status === s).length;
            return (
              <div key={s} className={`px-3 py-1 rounded-full border bg-slate-900
                                       ${STATUS_STYLES[s]} border-slate-700`}>
                {s}: {count}
              </div>
            );
          })}
          <div className="px-3 py-1 rounded-full border bg-slate-900 text-slate-400 border-slate-700">
            Total: {users.length}
          </div>
        </div>

        {/* Error */}
        {fetchError && (
          <div className="bg-red-950 border border-red-800 rounded-lg px-4 py-3 text-red-300 text-sm">
            {fetchError}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-900 border-b border-slate-800">
                {COLUMNS.map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="px-4 py-3 text-left text-slate-400 font-medium cursor-pointer
                               hover:text-cyan-400 select-none whitespace-nowrap transition-colors"
                  >
                    {col.label}
                    {sortKey === col.key && (
                      <span className="ml-1 text-cyan-400">
                        {sortDir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    Loading student data…
                  </td>
                </tr>
              ) : filteredSorted.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    {users.length === 0 ? "No students found." : "No results match your filters."}
                  </td>
                </tr>
              ) : (
                filteredSorted.map((row, i) => {
                  const tier = TIER_CONFIG[row.tier];
                  return (
                    <tr
                      key={row.emailId || i}
                      className="border-b border-slate-800/60 hover:bg-slate-900/60 transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-white">
                        {row.user || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-300 font-mono text-xs">
                        {row.emailId || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-200">
                        {row.progress}/{TOTAL_LEVELS}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-cyan-500 rounded-full"
                              style={{ width: `${row.completion}%` }}
                            />
                          </div>
                          <span className="text-slate-300 text-xs">{row.completion}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-200 font-mono">{row.totalScore}</span>
                          {tier && (
                            <span className={`px-2 py-0.5 rounded-full text-xs border font-medium
                                             ${tier.bg} ${tier.text} ${tier.border}`}>
                              {tier.label}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={`px-4 py-3 font-medium ${STATUS_STYLES[row.status]}`}>
                        {row.status}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filteredSorted.length > 0 && (
          <p className="text-slate-600 text-xs text-right">
            Showing {filteredSorted.length} of {users.length} student{users.length !== 1 ? "s" : ""}
          </p>
        )}
      </div>
    </div>
  );
}
